import { useState } from 'react'
import { Chip, Dropdown, Table } from '@heroui/react'

import { ChevronRightIcon, MoreIcon } from '@/components/ui/icons'

import type { ProductSummary } from '../types'
import { translated } from '../types'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export type ProductRowAction = 'edit' | 'publish' | 'unpublish' | 'enable' | 'disable' | 'delete'

interface ProductTableProps {
  products: ProductSummary[]
  onAction: (action: ProductRowAction, product: ProductSummary) => void
  /** Ids with a write in flight — their menu is held shut until it lands. */
  busyIds: string[]
}

/** Enabled and published are separate switches, so the row shows both. */
function StatusChips({ product }: { product: ProductSummary }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      <Chip size="sm" color={product.published ? 'success' : 'default'} variant="soft">
        {product.published ? 'Published' : 'Draft'}
      </Chip>
      {!product.enabled && (
        <Chip size="sm" color="warning" variant="soft">
          Disabled
        </Chip>
      )}
    </span>
  )
}

/** `Small · Red` — what this variant is, read off its option values. */
function variantLabel(variant: ProductSummary): string {
  const values = (variant.productAttributes ?? [])
    .map((attribute) => attribute.value?.trim())
    .filter(Boolean)

  return values.length ? values.join(' · ') : variant.sku || 'Variant'
}

/**
 * The outlets a product is carried at — two by name, the rest as a count, and
 * the whole list on hover.
 *
 * None at all is flagged rather than left blank: a product at no branch is on
 * no till, which is almost never what the merchant meant.
 *
 * Read-only. Which branches sell what is set on the Branches tab, or for one
 * product on its own form.
 */
function BranchesCell({ product }: { product: ProductSummary }) {
  // Not sent, as opposed to sent empty — see `ProductSummary.branches`.
  if (!product.branches) return <span className="text-muted text-xs">—</span>

  if (product.branches.length === 0) {
    return <span className="text-warning text-xs">No branch</span>
  }

  const names = product.branches.map((branch) => translated(branch.name, 'Branch'))

  return (
    <span className="text-muted text-xs" title={names.join(', ')}>
      {names.slice(0, 2).join(', ')}
      {names.length > 2 && ` +${names.length - 2}`}
    </span>
  )
}

/** The brand's logo and name, or a dash when the product has none. */
function BrandCell({ product }: { product: ProductSummary }) {
  if (!product.brand) return <span className="text-muted text-xs">—</span>

  const name = translated(product.brand.name)

  return (
    <span className="flex min-w-0 items-center gap-2" title={name}>
      {product.brand.logo?.url && (
        <img
          src={product.brand.logo.url}
          alt=""
          className="border-border size-6 shrink-0 rounded-md border object-contain"
        />
      )}
      <span className="text-muted truncate text-xs">{name}</span>
    </span>
  )
}

function stockCell(product: ProductSummary) {
  return product.type === 'stockable' ? (product.stockQuantity ?? 0) : '—'
}

export function ProductTable({ products, onAction, busyIds }: ProductTableProps) {
  // Expansion lives here rather than in the page: it is a way of looking at the
  // table, not a fact about the catalogue, and it should not survive a refetch
  // deciding to reorder the rows.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  function toggleExpanded(id: string) {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Products">
          <Table.Header>
            <Table.Column id="product" isRowHeader>
              Product
            </Table.Column>
            <Table.Column id="categories">Categories</Table.Column>
            <Table.Column id="brand">Brand</Table.Column>
            <Table.Column id="branches">Branches</Table.Column>
            <Table.Column id="price">Price</Table.Column>
            <Table.Column id="stock">Stock</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {products.flatMap((product) => {
              const variants = product.children ?? []
              const isExpanded = expandedIds.has(product.id)

              const rows = [
                <Table.Row key={product.id} id={product.id}>
                  <Table.Cell>
                    <span className="flex items-center gap-2">
                      {variants.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(product.id)}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} variants of ${translated(product.name)}`}
                          className="text-muted hover:text-foreground hover:bg-surface flex size-6 shrink-0 items-center justify-center rounded-md transition-colors"
                        >
                          <ChevronRightIcon
                            width={14}
                            height={14}
                            className={isExpanded ? 'rotate-90 transition-transform' : 'transition-transform'}
                          />
                        </button>
                      ) : (
                        // Keeps the names on one vertical line whether or not a
                        // product has variants to open.
                        <span className="size-6 shrink-0" />
                      )}

                      {product.thumbnail?.url ? (
                        <img
                          src={product.thumbnail.url}
                          alt=""
                          className="border-border size-10 shrink-0 rounded-lg border object-cover"
                        />
                      ) : (
                        <span className="border-border bg-surface size-10 shrink-0 rounded-lg border" />
                      )}

                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{translated(product.name)}</span>
                        <span className="text-muted truncate text-xs">
                          {product.sku || 'No SKU'}
                          {variants.length > 0 && ` · ${variants.length} variants`}
                        </span>
                      </span>
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="text-muted text-xs">
                      {product.categories.length
                        ? product.categories.map((category) => translated(category.name)).join(', ')
                        : '—'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <BrandCell product={product} />
                  </Table.Cell>

                  <Table.Cell>
                    <BranchesCell product={product} />
                  </Table.Cell>

                  <Table.Cell>
                    <span className="flex flex-col">
                      <span className="tabular-nums">{money.format(product.price ?? 0)}</span>
                      {/* A sale price only means something when it undercuts the list price. */}
                      {product.salePrice > 0 && product.salePrice < product.price && (
                        <span className="text-muted text-xs tabular-nums line-through">
                          {money.format(product.salePrice)}
                        </span>
                      )}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="tabular-nums">{stockCell(product)}</span>
                  </Table.Cell>

                  <Table.Cell>
                    <StatusChips product={product} />
                  </Table.Cell>

                  <Table.Cell>
                    <Dropdown>
                      {/* `Dropdown.Trigger` is itself a button — wrapping one inside it
                          nests two, which React rejects outright. */}
                      <Dropdown.Trigger
                        aria-label={`Actions for ${translated(product.name)}`}
                        isDisabled={busyIds.includes(product.id)}
                        className="text-muted hover:text-foreground hover:bg-surface inline-flex size-8 items-center justify-center rounded-full transition-colors disabled:opacity-50"
                      >
                        <MoreIcon width={16} height={16} />
                      </Dropdown.Trigger>
                      <Dropdown.Popover>
                        <Dropdown.Menu
                          onAction={(key) => onAction(key as ProductRowAction, product)}
                          aria-label="Product actions"
                        >
                          <Dropdown.Item id="edit">Edit</Dropdown.Item>
                          <Dropdown.Item id={product.published ? 'unpublish' : 'publish'}>
                            {product.published ? 'Unpublish' : 'Publish'}
                          </Dropdown.Item>
                          <Dropdown.Item id={product.enabled ? 'disable' : 'enable'}>
                            {product.enabled ? 'Disable' : 'Enable'}
                          </Dropdown.Item>
                          <Dropdown.Item id="delete">Delete</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown>
                  </Table.Cell>
                </Table.Row>,
              ]

              // Variants are rows of the same table rather than a nested one, so the
              // columns keep lining up with the parent's.
              if (isExpanded) {
                for (const variant of variants) {
                  rows.push(
                    <Table.Row key={variant.id} id={variant.id}>
                      <Table.Cell>
                        {/* 24px chevron + 40px thumbnail and their gaps — lines the
                            variant up under the parent's name rather than beside it. */}
                        <span className="flex items-center gap-2 ps-20">
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-sm">{variantLabel(variant)}</span>
                            <span className="text-muted truncate text-xs">
                              {variant.sku || 'No SKU'}
                            </span>
                          </span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {/* Categories are a property of the product, not the variant. */}
                        <span className="sr-only">Inherited from the product</span>
                      </Table.Cell>
                      <Table.Cell>
                        {/* So is the brand — a variant has no brand field of its own. */}
                        <span className="sr-only">Inherited from the product</span>
                      </Table.Cell>
                      <Table.Cell>
                        {/* Outlets too — a variant is carried wherever its product is. */}
                        <span className="sr-only">Inherited from the product</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="tabular-nums">{money.format(variant.price ?? 0)}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="tabular-nums">{stockCell(variant)}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-muted text-xs">
                          {variant.enabled ? '' : 'Disabled'}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {/* Variants are edited through the parent's form, so no menu. */}
                        <span className="sr-only">No actions</span>
                      </Table.Cell>
                    </Table.Row>,
                  )
                }
              }

              return rows
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
